import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    OnDestroy,
    Output
} from '@angular/core';
import {
    AbstractControl,
    UntypedFormGroup,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { merge } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { LoggingService } from '../shared/services/logging/logging.service';
import { AbstractNgModelComponent } from './abstract-ng-model-component';

@Component({
    template: '',
    standalone: false
})
export class AbstractFormGroupComponent
    extends AbstractNgModelComponent<UntypedFormGroup>
    implements Validator, OnDestroy {
    @Output() ValidChanged = new EventEmitter<boolean>();
    protected form: UntypedFormGroup;
    private _active = true;
    /** Notified by Angular to re-run NG_VALIDATORS when inner validity changes. */
    private _onValidatorChange: () => void = () => {};
    constructor(
        @Inject(LoggingService) protected log: LoggingService,
        protected cdRef: ChangeDetectorRef
    ) {
        super(log, cdRef);
    }
    writeValue(value: any): void {
        this.log.debug({
            type: 'AbstractFormGroupComponent:writeValue',
            value,
            form: this.form
        });
        // tslint:disable-next-line:no-unused-expression
        // value && this.form.setValue(value, { emitEvent: false });
        // tslint:disable-next-line:no-unused-expression
        value && this.form.setValue(value);
        setTimeout(() => {
            this.refreshFormValidity(false);
            this.cdRef.detectChanges();
        }, 0);
    }
    registerOnChange(fn: any): void {
        this.log.debug({
            type: 'AbstractFormGroupComponent:registerOnChange',
            fn
        });
        // Nested forms can become valid/invalid without a new value emission
        // (e.g. PrimeNG datepicker, nested validators). valueChanges alone does
        // not then update the parent FormControl, so Save stays disabled while
        // canClose/pristine still reflects edits.
        merge(this.form.valueChanges, this.form.statusChanges)
            .pipe(takeWhile(() => this._active))
            .subscribe(() => {
                fn(this.form.getRawValue());
                this._onValidatorChange();
                this.emitValidChanged();
            });
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }
    ngOnDestroy(): void {
        this._active = false;
    }
    validate(c: AbstractControl): ValidationErrors | null {
        // Disabled host/inner forms must not report INVALID (Angular leaves .valid false).
        if (c?.disabled || this.form?.disabled) {
            return null;
        }
        return this.form.status === 'VALID'
            ? null
            : this.form.errors || {
                  invalidForm: 'INVALID'
              };
    }

    /** Re-run CVA validation and notify parent FormControl (e.g. after enable/disable). */
    protected refreshFormValidity(emitEvent = true): void {
        if (!this.form) {
            return;
        }
        this.form.updateValueAndValidity({ emitEvent });
        this.notifyParentOfValidityChange();
    }

    /**
     * Notify parent CVA without calling updateValueAndValidity.
     * Use from status/value change handlers — registerOnChange already listens to those;
     * calling refreshFormValidity there causes an infinite feedback loop.
     */
    protected notifyParentOfValidityChange(): void {
        this._onValidatorChange();
        this.emitValidChanged();
    }

    protected emitValidChanged(): void {
        if (!this.form) {
            return;
        }
        const valid =
            this.form.disabled === true || this.form.status === 'VALID';
        this.ValidChanged.emit(valid);
    }
}
